import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

// material-ui
import Stack from '@mui/material/Stack';

// project import
import IconButton from 'components/@extended/IconButton';
import HeaderTooltip from './HeaderContent/HeaderTooltip';

// assets
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';

const FORWARD_LIMIT_KEY = 'historyNavMaxIdx';

// React Router's data router stores the current position in the history stack as `idx`.
const getHistoryIdx = () => window.history.state?.idx ?? 0;

const readMaxIdx = () => {
  try {
    return Number(window.sessionStorage.getItem(FORWARD_LIMIT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeMaxIdx = (value) => {
  try {
    window.sessionStorage.setItem(FORWARD_LIMIT_KEY, String(value));
  } catch {
    // sessionStorage unavailable; forward button just stays enabled less accurately
  }
};

// ==============================|| HEADER - BACK / FORWARD (for PWA standalone mode) ||============================== //

export default function HistoryNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [idx, setIdx] = useState(getHistoryIdx);
  const [maxIdx, setMaxIdx] = useState(() => Math.max(readMaxIdx(), getHistoryIdx()));

  useEffect(() => {
    const currentIdx = getHistoryIdx();
    setIdx(currentIdx);
    setMaxIdx((prevMax) => {
      // A new push discards any forward entries; going back/forward keeps them.
      const nextMax = navigationType === 'PUSH' ? currentIdx : Math.max(prevMax, currentIdx);
      writeMaxIdx(nextMax);
      return nextMax;
    });
  }, [location.key, navigationType]);

  const canGoBack = idx > 0;
  const canGoForward = idx < maxIdx;

  return (
    <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
      <HeaderTooltip title="Back">
        <span>
          <IconButton
            aria-label="Go back"
            color="secondary"
            variant="light"
            disabled={!canGoBack}
            onClick={() => navigate(-1)}
            sx={{ color: 'text.primary', bgcolor: 'transparent' }}
          >
            <ArrowLeftOutlined />
          </IconButton>
        </span>
      </HeaderTooltip>
      <HeaderTooltip title="Forward">
        <span>
          <IconButton
            aria-label="Go forward"
            color="secondary"
            variant="light"
            disabled={!canGoForward}
            onClick={() => navigate(1)}
            sx={{ color: 'text.primary', bgcolor: 'transparent' }}
          >
            <ArrowRightOutlined />
          </IconButton>
        </span>
      </HeaderTooltip>
    </Stack>
  );
}
